<birdz-card [isLoading]="isLoading">
  <div card-title>
    {{ (isEditMode ? 'QUERY_EDIT_TITLE' : 'CREATE_QUERY') | transloco }}
    @if (isDeleted) {
      <mat-chip class="chip-deleted" disableRipple>{{ 'QUERY_DELETED_CHIP' | transloco }}</mat-chip>
    }
  </div>
  <div card-corner>
    <button mat-icon-button type="button" [matTooltip]="'QUERY_HELP_BUTTON' | transloco" (click)="openHelp()">
      <mat-icon>help_outline</mat-icon>
    </button>
    @if (isEditMode) {
      <div class="corner-divider"></div>
      <button
        mat-icon-button
        type="button"
        [matTooltip]="'QUERY_CHANGELOG_BUTTON' | transloco"
        (click)="openChangelog()"
      >
        <mat-icon>manage_history</mat-icon>
      </button>
      <button mat-icon-button type="button" [matTooltip]="'QUERY_HISTORY_BUTTON' | transloco" (click)="openHistory()">
        <mat-icon>history</mat-icon>
      </button>
    }
  </div>
  <div card-body class="edit-body">
    <div class="edit-layout" [formGroup]="form">
      <!-- Left panel: metadata -->
      <div class="left-panel">
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>{{ 'QUERY_NAME' | transloco }}</mat-label>
          <input matInput formControlName="name" />
          @if (form.get('name')?.hasError('unique')) {
            <mat-error>{{ 'QUERY_NAME_ALREADY_USED' | transloco }}</mat-error>
          }
        </mat-form-field>

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>{{ 'QUERY_DESCRIPTION' | transloco }}</mat-label>
          <textarea matInput formControlName="description" rows="3"></textarea>
        </mat-form-field>

        <div class="database-icon-row">
          <mat-form-field appearance="outline" class="database-field">
            <mat-label>{{ 'QUERY_DATABASE' | transloco }}</mat-label>
            <mat-select formControlName="database_id">
              @for (db of databases; track db.id) {
                <mat-option [value]="db.id">{{ db.label }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
          <button
            mat-icon-button
            type="button"
            class="icon-picker-btn"
            [matTooltip]="'QUERY_ICON_CHOOSE' | transloco"
            (click)="openIconPicker()"
          >
            <mat-icon>{{ currentIcon || 'add_photo_alternate' }}</mat-icon>
          </button>
          <button
            mat-icon-button
            type="button"
            class="color-picker-btn"
            #colorMenuTrigger="matMenuTrigger"
            [matMenuTriggerFor]="colorMenu"
            [matTooltip]="'QUERY_COLOR_CHOOSE' | transloco"
          >
            <span class="color-dot-preview" [style.background]="currentColorBg" [style.color]="currentColorFg">
              <mat-icon>palette</mat-icon>
            </span>
          </button>
          <mat-menu #colorMenu="matMenu" panelClass="color-picker-menu">
            <div class="color-palette-grid" (click)="$event.stopPropagation()">
              @for (c of colorPalette; track c.key) {
                <button
                  type="button"
                  class="color-dot"
                  [style.background]="c.bg"
                  [style.color]="c.fg"
                  [class.color-dot--active]="currentColor === c.key"
                  [matTooltip]="c.label"
                  (click)="selectColor(c.key)"
                >
                  @if (currentColor === c.key) {
                    <mat-icon>check</mat-icon>
                  }
                </button>
              }
            </div>
          </mat-menu>
        </div>

        <!-- Tags chip input -->
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>{{ 'QUERY_TAGS' | transloco }}</mat-label>
          <mat-chip-grid #chipGrid>
            @for (tag of selectedTags; track tag) {
              <mat-chip-row (removed)="removeTag(tag)">
                {{ tag }}
                <button matChipRemove>
                  <mat-icon>cancel</mat-icon>
                </button>
              </mat-chip-row>
            }
          </mat-chip-grid>
          <input
            #tagInput
            [matChipInputFor]="chipGrid"
            [matChipInputSeparatorKeyCodes]="separatorKeysCodes"
            [matAutocomplete]="tagAuto"
            (matChipInputTokenEnd)="addTag($event.value)"
          />
          <mat-autocomplete #tagAuto (optionSelected)="onTagSelected($event)">
            @for (tag of availableTags; track tag) {
              @if (!selectedTags.includes(tag)) {
                <mat-option [value]="tag">{{ tag }}</mat-option>
              }
            }
          </mat-autocomplete>
        </mat-form-field>

        <!-- Auto-execute toggle -->
        <div class="lock-toggle">
          <mat-slide-toggle formControlName="auto_execute">
            {{ 'QUERY_AUTO_EXECUTE' | transloco }}
          </mat-slide-toggle>
          <button
            mat-icon-button
            type="button"
            [matTooltip]="'QUERY_AUTO_EXECUTE_INFO' | transloco"
            matTooltipClass="help-tooltip"
          >
            <mat-icon>help_outline</mat-icon>
          </button>
        </div>

        <!-- Lock toggle: only visible to creator -->
        @if (isCreator) {
          <div class="lock-toggle">
            <mat-slide-toggle formControlName="is_locked">
              {{ 'QUERY_LOCKED' | transloco }}
            </mat-slide-toggle>
            <button
              mat-icon-button
              type="button"
              [matTooltip]="'QUERY_LOCKED_INFO' | transloco"
              matTooltipClass="help-tooltip"
            >
              <mat-icon>help_outline</mat-icon>
            </button>
          </div>
        }

        <!-- Visibility toggle: only visible to creator in edit mode -->
        @if (isCreator) {
          <div class="lock-toggle">
            <mat-slide-toggle formControlName="is_private">
              {{ 'QUERY_PRIVATE' | transloco }}
            </mat-slide-toggle>
            <button
              mat-icon-button
              type="button"
              [matTooltip]="'QUERY_PRIVATE_INFO' | transloco"
              matTooltipClass="help-tooltip"
            >
              <mat-icon>help_outline</mat-icon>
            </button>
          </div>
        }
      </div>

      <!-- Right panel: SQL editor -->
      <div class="right-panel">
        @if (form.get('database_id')?.value === 'bigquery' && !bigqueryInfoDismissed) {
          <div class="bigquery-info">
            <mat-icon>info</mat-icon>
            <div>
              <span>{{ 'QUERY_BIGQUERY_ACCESS_INFO' | transloco }}</span>
              <code class="bigquery-service-account">{{ bigqueryServiceAccount }}</code>
            </div>
            <button mat-icon-button type="button" class="bigquery-info-close" (click)="bigqueryInfoDismissed = true">
              <mat-icon>close</mat-icon>
            </button>
          </div>
        }
        <div class="sql-toolbar">
          <span class="sql-label">{{ 'QUERY_SQL' | transloco }}</span>
          <button
            mat-stroked-button
            type="button"
            [matTooltip]="'QUERY_FIELD_TEXT_TOOLTIP' | transloco"
            matTooltipPosition="above"
            (click)="insertTemplateField('text')"
          >
            <mat-icon>text_fields</mat-icon>
            text
          </button>
          <button
            mat-stroked-button
            type="button"
            [matTooltip]="'QUERY_FIELD_NUMBER_TOOLTIP' | transloco"
            matTooltipPosition="above"
            (click)="insertTemplateField('number')"
          >
            <mat-icon>pin</mat-icon>
            number
          </button>
          <button
            mat-stroked-button
            type="button"
            [matTooltip]="'QUERY_FIELD_TEXTLIST_TOOLTIP' | transloco"
            matTooltipPosition="above"
            (click)="insertTemplateField('textlist')"
          >
            <mat-icon>list</mat-icon>
            textlist
          </button>
          <button
            mat-stroked-button
            type="button"
            [matTooltip]="'QUERY_FIELD_NUMBERLIST_TOOLTIP' | transloco"
            matTooltipPosition="above"
            (click)="insertTemplateField('numberlist')"
          >
            <mat-icon>format_list_numbered</mat-icon>
            numberlist
          </button>
          <button
            mat-stroked-button
            type="button"
            [matTooltip]="'QUERY_FIELD_DATE_TOOLTIP' | transloco"
            matTooltipPosition="above"
            (click)="insertTemplateField('date')"
          >
            <mat-icon>calendar_today</mat-icon>
            date
          </button>
          <button
            mat-stroked-button
            type="button"
            [matTooltip]="'QUERY_FIELD_ENV_TOOLTIP' | transloco"
            matTooltipPosition="above"
            (click)="insertEnvField()"
          >
            <mat-icon>layers</mat-icon>
            {{ 'QUERY_ENV_FIELD' | transloco }}
          </button>
          <button
            mat-stroked-button
            type="button"
            [matTooltip]="'QUERY_FIELD_LINK_TOOLTIP' | transloco"
            matTooltipPosition="above"
            (click)="insertTemplateField('link')"
          >
            <mat-icon>link</mat-icon>
            link
          </button>
        </div>
        <mat-form-field appearance="outline" class="full-width sql-field">
          <textarea
            #sqlTextarea
            matInput
            formControlName="sql"
            class="sql-textarea"
            spellcheck="false"
            autocomplete="off"
          ></textarea>
          @if (form.get('sql')?.hasError('selectOnly')) {
            <mat-error>{{ 'QUERY_SQL_SELECT_ONLY' | transloco }}</mat-error>
          }
        </mat-form-field>
      </div>
    </div>
    <div class="edit-footer">
      <button mat-stroked-button type="button" (click)="cancel()">{{ 'CANCEL' | transloco }}</button>
      @if (isEditor && isEditMode && !isDeleted) {
        <button
          mat-stroked-button
          color="warn"
          type="button"
          [disabled]="(query?.is_locked && !isCreator) || isDeleting"
          [matTooltip]="query?.is_locked && !isCreator ? ('QUERY_LOCKED_TOOLTIP' | transloco) : ''"
          (click)="deleteQuery()"
        >
          <mat-icon>delete</mat-icon>
          {{ 'QUERY_DELETE' | transloco }}
        </button>
      }
      @if (isDeleted) {
        <button mat-flat-button color="warn" type="button" [disabled]="isRestoring" (click)="restore()">
          @if (isRestoring) {
            <mat-spinner diameter="18"></mat-spinner>
          } @else {
            <mat-icon>restore</mat-icon>
          }
          {{ 'QUERY_RESTORE' | transloco }}
        </button>
      } @else {
        <button mat-flat-button color="primary" type="button" [disabled]="form.invalid || isSaving" (click)="save()">
          @if (isSaving) {
            <mat-spinner diameter="18"></mat-spinner>
          } @else {
            {{ 'SAVE' | transloco }}
          }
        </button>
      }
    </div>
  </div>
</birdz-card>
